import { graphqlFetch } from '@/utils/graphqlFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateTipsCategoryInput,
  CreateTipsInput,
  MutationUpdateTipArgs,
  TipOutput,
  TipsCategoryOutput,
} from '../gql/graphql';
import {
  CREATE_TIP,
  CREATE_TIP_BY_CATEGORY,
  REMOVE_TIP,
  UPDATE_TIP,
} from '../mutations';
import { GET_TIPS_BY_CATEGORY, GET_TIPS_CATEGORIES } from '../queries';

export const useGetTipsCategories = () => {
  return useQuery<TipsCategoryOutput[], Error>({
    queryKey: ['tipsCategories'],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getTipsCategories: TipsCategoryOutput[] },
        null
      >(GET_TIPS_CATEGORIES);
      return data.getTipsCategories;
    },
  });
};

export const useGetTipsByCategory = (category: string) => {
  return useQuery<TipOutput[], Error>({
    queryKey: ['tips', category],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getTipsByCategory: TipOutput[] },
        { category: string }
      >(GET_TIPS_BY_CATEGORY, { category });
      return data.getTipsByCategory;
    },
    enabled: !!category,
  });
};

export const useAddTipByCategory = () => {
  const queryClient = useQueryClient();
  return useMutation<TipsCategoryOutput, Error, CreateTipsCategoryInput>({
    mutationFn: async (tip) => {
      const data = await graphqlFetch<
        { createTipsCategory: TipsCategoryOutput },
        { input: CreateTipsCategoryInput }
      >(CREATE_TIP_BY_CATEGORY, { input: tip });
      return data.createTipsCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipsCategories'] });
    },
  });
};

export const useAddTip = () => {
  const queryClient = useQueryClient();
  return useMutation<TipOutput, Error, CreateTipsInput>({
    mutationFn: async (tip) => {
      const data = await graphqlFetch<
        { createTip: TipOutput },
        { input: CreateTipsInput }
      >(CREATE_TIP, { input: tip });
      return data.createTip;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tips'] });
    },
  });
};

export const useUpdateTip = () => {
  const queryClient = useQueryClient();
  return useMutation<TipOutput, Error, MutationUpdateTipArgs>({
    mutationFn: async (tip) => {
      const data = await graphqlFetch<
        { updateTip: TipOutput },
        MutationUpdateTipArgs
      >(UPDATE_TIP, { id: tip.id, input: tip.input });
      return data.updateTip;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tips'] });
    },
  });
};

export const useRemoveTip = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await graphqlFetch<{ removeTip: TipOutput }, { id: string }>(
        REMOVE_TIP,
        { id },
      );
      return data.removeTip;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['tips'],
      }),
  });
};
